import type { DatePickerProps as MuiDatePickerProps } from '@mui/x-date-pickers';

import { DatePicker as MuiDatePicker } from '@mui/x-date-pickers';
import classNames from 'classnames';
import React from 'react';

import { getMonthMatrix } from '../../utils/getMonthMatrix';
import { HelperMessage } from '../HelperMessage';
import { ReadOnlyField } from '../ReadOnlyField';
import { ErrorMessage } from '../ErrorMessage';
import { format } from '../../utils/dates';
import { useIntl } from '../../context';

export interface DatePickerProps<
  T1 extends Date,
  T2 extends boolean,
> extends MuiDatePickerProps<T1, T2> {
  errors?: string | string[];
  fullWidth?: boolean;
  helperText?: string;
}

// TODO Should be based on UTC
const initialDate = new Date();
const DatePickerWrapper = <T1 extends Date, T2 extends boolean>(
  {
    className,
    defaultValue,
    errors,
    fullWidth,
    helperText,
    onMonthChange: onMonthChangeProp,
    onVisibleDatesChange,
    readOnly,
    ...props
  }: {
    onVisibleDatesChange?: (dates: Date[]) => void;
  } & DatePickerProps<T1, T2>,
  ref: React.ForwardedRef<HTMLDivElement>,
) => {
  const { locale } = useIntl();
  const [selected, setSelected] = React.useState<T1>(
    (defaultValue ?? initialDate) as T1,
  );

  const visibleDatesChangeRef = React.useRef(onVisibleDatesChange);
  const monthChangeRef = React.useRef(onMonthChangeProp);

  React.useEffect(() => {
    visibleDatesChangeRef.current = onVisibleDatesChange;
  }, [onVisibleDatesChange]);

  React.useEffect(() => {
    monthChangeRef.current = onMonthChangeProp;
  }, [onMonthChangeProp]);

  const onMonthChange = React.useCallback((date: Date) => {
    const visibleDates = getMonthMatrix(date);
    visibleDatesChangeRef.current?.(visibleDates);
    monthChangeRef.current?.(date as T1);
  }, []);

  const handleChange = (date: T1 | null) => {
    setSelected(date as T1);
  };

  React.useEffect(() => {
    if (selected) {
      const visibleDates = getMonthMatrix(selected);
      visibleDatesChangeRef.current?.(visibleDates);
    }
  }, [selected]);

  if (readOnly) {
    // The caller's own props only — never the internal `selected`, whose
    // fallback (today) made a NULL-valued readOnly field RENDER today's date.
    // Of the two, the controlled `value` wins whenever the caller passes one:
    // the Form wrappers pass both, and a field updated through setValue()
    // rather than reset() leaves defaultValue untouched, so reading
    // defaultValue alone showed stale data. `undefined` means "not
    // controlled" and falls back to defaultValue; an explicit `null` renders
    // EMPTY — that is the whole point of the fix above.
    const displayed = props.value === undefined ? defaultValue : props.value;
    // Legacy forms keep ISO STRINGS in RHF state and the Form wrappers cast
    // them straight through, so `displayed` is not always a Date. Coerce
    // before the NaN guard — `.getTime()` on a string is a render crash.
    const displayedDate =
      typeof displayed === 'string' ? new Date(displayed) : displayed;
    const displayedLabel =
      displayedDate && !Number.isNaN(displayedDate.getTime())
        ? format(displayedDate, { locale, withoutTime: true })
        : '';

    return (
      <ReadOnlyField
        value={displayedLabel}
        label={props?.label ?? ''}
        className={className}
        fullWidth={fullWidth}
        errors={errors}
      />
    );
  }

  return (
    <div
      className={classNames(
        {
          'w-full': fullWidth,
        },
        className,
      )}
    >
      <MuiDatePicker
        className={classNames({ 'w-full': fullWidth })}
        onMonthChange={onMonthChange}
        onChange={handleChange}
        // The caller's defaultValue only — feeding the internal `selected`
        // fallback (today) made a NULL-valued field RENDER today's date, so
        // users saw an end date that was never in the form state.
        defaultValue={defaultValue as T1}
        ref={ref}
        {...props}
        // `disableEnforceFocus` — the desktop popper is wrapped in a focus trap
        // that MUI X declares as `isEnabled: () => true`, so it NEVER yields:
        // whenever the focus lands outside the popper it synchronously calls
        // `focus()` on its own paper. A second trap that also enforces and sits
        // in a DISJOINT React tree (any MUI Dialog/Menu/Select/Popover, or a
        // second picker's popper) does the same in the `focusin` handler that
        // call fires, so the two steal focus from each other without ever
        // unwinding — `RangeError: Maximum call stack size exceeded`
        // (Sentry MYFITWORLD-V2-6H). MUI's own guard against this only covers a
        // trap nested INSIDE another trap's React tree, which siblings are not.
        // Safari is where it actually fires: clicking a button there leaves
        // `document.activeElement` on `<body>`, and FocusTrap's 50ms interval
        // then runs the containment of every open trap with no user event at
        // all. Turning enforcement off costs nothing here — pickers manage
        // their own focus (`disableAutoFocus`/`disableRestoreFocus`) and Tab
        // containment still works through the trap's sentinel nodes.
        // Merged, not defaulted: `useDesktopPicker` always hands `slotProps`
        // down to `PickersPopper`, so a theme-level default would be replaced.
        slotProps={{
          ...props.slotProps,
          desktopTrapFocus: {
            disableEnforceFocus: true,
            ...props.slotProps?.desktopTrapFocus,
          },
        }}
      />
      {helperText && (
        <HelperMessage className="mt-1 ">{helperText}</HelperMessage>
      )}
      {errors && <ErrorMessage className="mt-1" errors={errors} />}
    </div>
  );
};

export const DatePicker = React.forwardRef(DatePickerWrapper);

DatePicker.displayName = 'DatePicker';
